// src/components/ExportDomain.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import "./ExportDomain.css";
import { baseURL } from "../../env/env";
import { Spinner } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";

const ExportDomain = () => {
  // States for Export History
  const [clients, setClients] = useState([]);
  const [dates, setDates] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [instances, setInstances] = useState([]);
  const [values, setValues] = useState([]);
  const [instanceLoading, setInstanceLoading] = useState(false);
  const [dateOptions, setDateOptions] = useState([]);

  // States for Domain History
  const [users, setUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [domainData, setDomainData] = useState([]);
  const [isDomainLoading, setIsDomainLoading] = useState(false);

  // API Base URL
  const API_BASE_URL_DOC =
    "https://mattersmith6.embeddedexperience.com/doc-tracker-api";

  // Fetch clients on component mount
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await axios.get(`${baseURL}/exported-clients/`);
        setClients(response.data.clients || []);
      } catch (error) {
        console.error("Error fetching clients:", error);
        toast.error("Something went wrong while fetching clients");
      }
    };

    fetchClients();
  }, []);

  // Fetch dates and values when selectedClient changes
  useEffect(() => {
    const fetchDates = async () => {
      if (selectedClient) {
        try {
          const response = await axios.post(`${baseURL}/exported-date`, {
            name: selectedClient,
          });
          setDates(response.data.date || []);
          setValues(response.data.values || []);
        } catch (error) {
          console.error("Error fetching dates:", error);
          toast.error("Something went wrong while fetching dates");
        }
      }
    };

    fetchDates();
  }, [selectedClient]);

  useEffect(() => {
    const dateOptions = dates.map((date, index) => ({
      value: values[index], // Set value from corresponding index in values array
      label: date, // Set label as the date
    }));
    setDateOptions(dateOptions || []);
  }, [dates, values]);

  // Fetch instances when selectedClient and selectedDate change
  useEffect(() => {
    const fetchInstances = async () => {
      if (selectedClient && selectedDate) {
        try {
          setInstanceLoading(true); // Start loader

          const response = await axios.post(`${baseURL}/exported-data/`, {
            name: selectedClient,
            date: selectedDate,
          });

          setInstances(response.data.instances || []);
        } catch (error) {
          console.error("Error fetching instances:", error);
          toast.error("Something went wrong while fetching instances");
        } finally {
          setInstanceLoading(false); // Stop loader once data is fetched
        }
      }
    };

    fetchInstances();
  }, [selectedClient, selectedDate, dates, values]);

  // Fetch users based on role
  useEffect(() => {
    const fetchUsers = async () => {
      if (selectedRole) {
        try {
          const response = await axios.get(
            `${API_BASE_URL_DOC}/users/?page_no=1&limits=10`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              },
            }
          );
          const userData = response.data;
          const filteredUsers = userData.filter(
            (user) => user.is_staff === selectedRole
          );
          setUsers(filteredUsers);
        } catch (error) {
          console.error("Error fetching users:", error);
          toast.error("Something went wrong while fetching users");
        }
      }
    };

    fetchUsers();
  }, [selectedRole]);

  // Fetch domain data for selected user
  useEffect(() => {
    const fetchDomainData = async () => {
      if (selectedUser) {
        setIsDomainLoading(true); // Show loader
        try {
          const response = await axios.get(
            `${baseURL}/domain-data/${selectedUser}/`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              },
            }
          );
          setDomainData(response.data);
        } catch (error) {
          console.error("Error fetching domain data:", error);
          toast.error("Something went wrong while fetching domain data");
        } finally {
          setIsDomainLoading(false); // Hide loader
        }
      }
    };

    fetchDomainData();
  }, [selectedUser]);

  // Clear all selections
  const handleClear = () => {
    setSelectedClient(null);
    setSelectedDate(null);
    setInstances([]);
    setDateOptions([]);
  };

  // Clear all selections for Domain History
  const handleClearDomainHistory = () => {
    setSelectedRole(null);
    setSelectedUser(null);
    setDomainData([]);
    setUsers([]);
  };

  const clientOptions = clients.map((client) => ({
    value: client,
    label: client,
  }));

  const handleClientChange = (selectedOption) => {
    const newClient = selectedOption ? selectedOption.value : null;

    if (newClient !== selectedClient) {
      setSelectedClient(newClient);
      setSelectedDate(null); // Reset selected date only when the client changes
      setInstances([]);
      setDateOptions([]);
    }
  };

  const calculateWidth = (options) => {
    const longestLabel = options.reduce((max, option) => {
      return option.label.length > max.length ? option.label : max;
    }, "");
    const width = longestLabel.length * 7; // Example multiplier for the width
    return `${width + 40}px`; // Add some padding to the width
  };

  const clientWidth = calculateWidth(clientOptions);

  const handleChange = (selectedOption) => {
    setSelectedDate(selectedOption ? selectedOption.value : "");
  };

  const userOptions = users.map((user) => ({
    value: user.id,
    label: user.email,
  }));

  const handleUserChange = (selectedOption) => {
    setSelectedUser(selectedOption ? selectedOption.value : "");
  };

  const roleOptions = [
    { value: "Editor", label: "Editor" },
    { value: "Viewer", label: "Viewer" },
  ];

  const handleRoleChange = (selectedOption) => {
    setSelectedRole(selectedOption ? selectedOption.value : "");
    setDomainData([]);
  };

  const customStyles = (width) => ({
    control: (provided) => ({
      ...provided,
      fontSize: "0.875rem", // Reduce font size for the control (dropdown box)
      // width: "200px", // Adjust control width as needed
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: "0.875rem", // Reduce font size for the selected value
    }),
    option: (provided) => ({
      ...provided,
      fontSize: "0.875rem", // Reduce font size for the options in the dropdown
    }),
    menu: (provided) => ({
      ...provided,
      width: width || "240px", // Set width dynamically if clientWidth is provided, else use default
    }),
  });

  return (
    <div className="export-domain-container">
      {/* Main content area */}
      <div className="content-area">
        {/* Left side for Export History */}
        <div className="export-history">
          <h2>Export History</h2>
          {/* Dropdowns for Client and Date selection */}

          <div className="export-dropdown-container">
            {/* Client Dropdown */}
            <div className="export-dropdown">
              <Select
                id="client-select"
                value={
                  clientOptions.find(
                    (option) => option.value === selectedClient
                  ) || null
                }
                onChange={handleClientChange}
                options={clientOptions}
                placeholder="Select Client"
                isSearchable={false} // Disable search functionality
                styles={customStyles(clientWidth)}
              />
            </div>

            {/* Date Dropdown */}
            <div className="export-dropdown">
              <Select
                id="date-select"
                value={
                  dateOptions.find((option) => option.value === selectedDate) ||
                  null
                }
                onChange={handleChange}
                options={dateOptions}
                placeholder="Select Date"
                isSearchable={false}
                styles={customStyles()}
              />
            </div>
          </div>

          {instanceLoading ? (
            <div
              className="instances-list"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Spinner style={{ fontSize: "12px" }} />
            </div>
          ) : (
            instances.length > 0 && (
              <div className="instances-list">
                <ul>
                  {instances.map((instance, index) => (
                    <li key={index}>
                      {" "}
                      {/* Use a unique identifier if available */}
                      {instance.inst_name} <b>(:{instance.cls_name})</b>
                    </li>
                  ))}
                </ul>
              </div>
            )
          )}
          <button className="clear-button" onClick={handleClear}>
            Clear
          </button>
        </div>

        {/* Right side for Domain History */}
        <div className="domain-history">
          <h2>Domain History</h2>
          {/* Dropdowns for Role and User selection */}
          <div className="export-dropdown-container">
            {/* Role Dropdown */}
            <div className="export-dropdown">
              <Select
                id="role-select"
                value={
                  roleOptions.find((option) => option.value === selectedRole) ||
                  null
                }
                onChange={handleRoleChange}
                options={roleOptions}
                placeholder="Select Role"
                isSearchable={false} // Disable search for a simple dropdown
                styles={customStyles()}
              />
            </div>

            {/* User Dropdown */}
            <div className="export-dropdown">
              <Select
                id="user-select"
                value={
                  userOptions.find((option) => option.value === selectedUser) ||
                  null
                }
                onChange={handleUserChange}
                options={userOptions}
                placeholder="Select User"
                isSearchable={false} // Disable search if you don't need it
                styles={customStyles()}
              />
            </div>
          </div>
          {isDomainLoading ? (
            <div
              className="instances-list"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Spinner style={{ fontSize: "12px" }} />
            </div>
          ) : (
            <div className="domain-data">
              {domainData.allowed && (
                <div>
                  <h3>Allowed:</h3>
                  <ul>
                    {domainData.allowed.map((item, index) => (
                      <li key={index}>
                        {item.inst_name} <b>(:{item.cls_name})</b>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {domainData.restricted && (
                <div>
                  <h3>Restricted:</h3>
                  <ul>
                    {domainData.restricted.map((item, index) => (
                      <li key={index}>
                        {item.inst_name} <b>(:{item.cls_name})</b>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <button className="clear-button" onClick={handleClearDomainHistory}>
            Clear
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportDomain;
